import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { RingAndRelatedProductsIdMap } from 'content/products/ring';
import { uhxVariantIds } from 'content/products/uhx/variantIds';
import {
  getMaxConfig,
  getMaxVariantIds,
  MaxPlanKey,
  resolveMaxPlanKey,
} from 'content/products/max';
import { ProductType, Node, isDigitalProductType } from './types';

type CartLike = Record<string, any> | null;

const lineAttr = (node: any, key: string): string | undefined =>
  node?.attributes?.find((a: { key: string }) => a.key === key)?.value;

/** Cart product type for discount tiering, derived from cart contents: RING_PRO → RING → M1. */
export function deriveCartType(
  cart: CartLike,
  store: ShopifyStore | null,
): ProductType | undefined {
  const edges = cart?.lines?.edges;
  if (!cart || !store || !Array.isArray(edges) || edges.length === 0) {
    return undefined;
  }
  const hasRingPro = edges.some(({ node }: any) => {
    const type = lineAttr(node, '_product_type');
    return type === ProductType.RING_PRO || type === 'ring_pro';
  });
  if (hasRingPro) return ProductType.RING_PRO;
  const hasRing = edges.some(({ node }: any) =>
    RingAndRelatedProductsIdMap[store]?.includes(node?.merchandise?.product?.id),
  );
  return hasRing ? ProductType.RING : ProductType.M1;
}

/** The in-cart UHX subscription line (Year1/Year2/Month3), or null. */
export function findUhxLine(
  cart: CartLike,
  store: ShopifyStore | null,
): Node | null {
  const edges = cart?.lines?.edges;
  const ids = store ? uhxVariantIds[store] : undefined;
  if (!ids || !Array.isArray(edges)) return null;
  for (const { node } of edges) {
    const id = node?.merchandise?.id;
    if (id === ids.Year1 || id === ids.Year2 || id === ids.Month3) {
      return node as Node;
    }
  }
  return null;
}

/** The in-cart Ultrahuman MAX subscription line, or null. */
export function findMaxLine(
  cart: CartLike,
  store: ShopifyStore | null,
): Node | null {
  const edges = cart?.lines?.edges;
  const cfg = getMaxConfig(store);
  if (!cfg || !Array.isArray(edges)) return null;
  const ids = new Set(getMaxVariantIds(cfg));
  const hit = edges.find(({ node }: any) => ids.has(node?.merchandise?.id));
  return (hit?.node as Node) ?? null;
}

/** Monthly or yearly for a MAX line; null for non-MAX lines. */
export function getMaxPlanKey(
  node: Record<string, any> | null | undefined,
  store: ShopifyStore | null,
): MaxPlanKey | null {
  return resolveMaxPlanKey(
    node?.sellingPlanAllocation?.sellingPlan,
    store,
    node?.merchandise?.id,
  );
}

/** True when the cart is non-empty and every line is a digital product. */
export function deriveIsAllDigital(cart: CartLike): boolean {
  const edges = cart?.lines?.edges;
  if (!Array.isArray(edges) || edges.length === 0) return false;
  return edges.every(({ node }: any) =>
    isDigitalProductType(lineAttr(node, '_product_type')),
  );
}
